import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { FlexColumn } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { emptyArray } from 'ui-components/default-props/empty';

import { getDomains } from './api-client';
import { DomainStatus } from './domain-status';
import { messages } from './messages';
import { SenderEmailsTable } from './sender-emails-table';
import style from './sender-emails.module.less';
import HelpArticleLink from '../../../components/help-article-link';
import { useOnComponentMount } from '../../../hooks/use-on-component-mount';
import { linkIDs } from '../../../services/link-service/links';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';

export const SenderEmails = () => {
  const [domainData, setDomainData] = useState({
    stores: emptyArray,
    domains: emptyArray,
  });
  const [isLoadingDomains, setIsLoadingDomains] = useState(false);
  const [hasErrorLoadingDomains, setHasErrorLoadingDomains] = useState(false);

  const loadDomains = useCallback(() => {
    setIsLoadingDomains(true);
    setHasErrorLoadingDomains(false);

    getDomains()
      .then((response) => {
        setDomainData(response.data);
      })
      .catch(() => {
        setHasErrorLoadingDomains(true);
      })
      .finally(() => {
        setIsLoadingDomains(false);
      });
  }, []);

  useOnComponentMount(loadDomains);

  const updateDomainData = useCallback((updatedDomain) => {
    setDomainData((prev) => {
      const newDomains = [...prev.domains];

      const domainIndex = newDomains.findIndex((domain) => domain.id === updatedDomain.id);
      if (domainIndex !== -1) {
        newDomains[domainIndex] = updatedDomain;
      } else {
        newDomains.push(updatedDomain);
      }

      return {
        stores: prev.stores,
        domains: newDomains,
      };
    });
  }, []);

  const data = useMemo(() => {
    const { stores, domains } = domainData;

    return stores?.reduce((acc, store) => {
      if (store.email) {
        const emailDomainName = store.email.split('@')[1];
        const domainForEmail =
          domains?.find((domain) => {
            return domain.name === emailDomainName;
          }) || {};

        acc.push({
          storeId: store.storeId,
          email: store.email,
          emailVerification: store.emailVerification,
          domainVerificationStatus: store.isSupported ? domainForEmail.domainVerificationStatus : DomainStatus.Unsupported,
          dnsRecords: domainForEmail.dnsRecords,
          name: domainForEmail.name,
          domainId: domainForEmail.id,
        });
      }
      return acc;
    }, []);
  }, [domainData]);

  return (
    <>
      <FlexColumn className={style['sender-emails-page']}>
        <div>
          <SettingsPageTitle value={messages.senderEmailsTitle} />
          <FlexColumn className={style['text-section']}>
            <Text textType={TextType.Description} value={messages.senderEmailInstructions} />
            <BannerMessageV2
              content={<Text textType={TextType.Paragraph} value={messages.senderEmailsBanner} />}
              layout={BannerLayout.Full}
              status={BannerStatus.Info}
            />
            <HelpArticleLink articleId={linkIDs.SenderEmails} className={style['help-article-link']}>
              <FormattedMessage {...messages.learnMoreAboutDomainVerification} />
            </HelpArticleLink>
          </FlexColumn>
        </div>
        {hasErrorLoadingDomains ? (
          <BannerMessageV2
            content={<Text textType={TextType.Paragraph} value={messages.domainLoadingError} />}
            layout={BannerLayout.Full}
            status={BannerStatus.Error}
          />
        ) : (
          <SenderEmailsTable
            domains={data}
            isLoadingDomains={isLoadingDomains}
            reloadDomains={loadDomains}
            updateDomainData={updateDomainData}
          />
        )}
      </FlexColumn>
    </>
  );
};
